import React, { useEffect, useState } from "react";
import GameLauncher from "./GameLauncher";
import SteamGameLauncher from "./SteamGameLauncher";
import DownloadableGameLauncher from "./DownloadableGameLauncher";
import ExternalWebGameLauncher from "./ExternalWebGameLauncher";
import { checkGameEmbeddable } from "../../services/gameLaunchService";
import { isSteamStoreUrl } from "../../utils/steamUtils";
import "./GameLaunchModal.scss";

/**
 * Game Launch Modal Component
 * Modal wrapper for game launcher with full-screen iframe or external launch
 */
function GameLaunchModal({ assignment, isOpen, onClose }) {
  const [error, setError] = useState(null);
  const [embedCheck, setEmbedCheck] = useState({
    status: "idle", // idle | checking | ready
    canEmbed: true,
    reason: null,
  });
  const [forceExternal, setForceExternal] = useState(false);

  const isSteamAssignment = isSteamStoreUrl(assignment?.webUrl);
  const isFileAssignment = assignment?.buildType === "file";
  const isWebAssignment = !isFileAssignment && !isSteamAssignment;

  useEffect(() => {
    let cancelled = false;

    const runEmbedCheck = async () => {
      if (!isOpen || !assignment || !isWebAssignment) {
        setEmbedCheck({ status: "ready", canEmbed: true, reason: null });
        setForceExternal(false);
        return;
      }

      setForceExternal(false);
      setEmbedCheck({ status: "checking", canEmbed: true, reason: null });
      const result = await checkGameEmbeddable(assignment.webUrl);
      if (cancelled) return;
      setEmbedCheck({
        status: "ready",
        canEmbed: result.canEmbed !== false,
        reason: result.reason || null,
      });
    };

    runEmbedCheck();
    return () => {
      cancelled = true;
    };
  }, [isOpen, assignment, isWebAssignment, assignment?.webUrl]);

  if (!isOpen || !assignment) {
    return null;
  }

  const handleClose = async () => {
    setError(null);
    setForceExternal(false);
    if (onClose) {
      onClose();
    }
  };

  const handleError = (errorMsg) => {
    setError(errorMsg);
  };

  const useExternalWeb =
    isWebAssignment &&
    (forceExternal || (embedCheck.status === "ready" && embedCheck.canEmbed === false));

  return (
    <div className="game-launch-modal-overlay" onClick={handleClose}>
      <div className="game-launch-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-title">
            <h2>{assignment.gameName || "Game"}</h2>
            {assignment.deadline && (
              <span className="deadline-badge">
                Deadline: {new Date(assignment.deadline).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="modal-header-actions">
            <button className="modal-close" onClick={handleClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="modal-content">
          {error ? (
            <div className="modal-error">
              <div className="error-icon">⚠️</div>
              <h3>Error Launching Game</h3>
              <p>{error}</p>
              {error.includes("not configured") ? (
                <div className="error-actions">
                  <p className="error-hint">
                    This game's authentication is not configured. Please contact support.
                  </p>
                  <button className="btn btn-primary" onClick={handleClose}>
                    Close
                  </button>
                </div>
              ) : (
                <div className="error-actions">
                  <button className="btn btn-secondary" onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}>
                    Retry
                  </button>
                  <button className="btn btn-primary" onClick={handleClose}>
                    Close
                  </button>
                </div>
              )}
            </div>
          ) : isFileAssignment ? (
            <DownloadableGameLauncher assignment={assignment} onClose={handleClose} onError={handleError} />
          ) : isSteamAssignment ? (
            <SteamGameLauncher assignment={assignment} onClose={handleClose} onError={handleError} />
          ) : embedCheck.status === "checking" ? (
            <div className="modal-embed-checking">
              <p>Checking whether this game can open inside Immersely…</p>
            </div>
          ) : useExternalWeb ? (
            <ExternalWebGameLauncher
              assignment={assignment}
              onClose={handleClose}
              onError={handleError}
            />
          ) : (
            <GameLauncher
              assignment={assignment}
              onClose={handleClose}
              onError={handleError}
              onRequestExternal={() => setForceExternal(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default GameLaunchModal;
